/**
 * Simplified to Full Format Converter
 *
 * Converts simplified (LLM-friendly) JSON format to full (editor-compatible) format.
 * Handles nested conditional branches, generates IDs, and positions nodes.
 */

import {
  WorkflowJSON,
  SimplifiedWorkflowJSON,
  SimplifiedStepJSON,
  WorkflowNodeJSON,
  WorkflowEdgeJSON,
  WorkflowNodeData,
  AUTO_LAYOUT_CONSTANTS,
} from '../types';
import { SequentialIdGenerator } from './idGeneratorService';
import { autoLayoutNodes } from './autoLayoutService';

const { START_X, START_Y, NODE_HEIGHT, GAP_Y, BRANCH_OFFSET_X } = AUTO_LAYOUT_CONSTANTS;

/**
 * Options for conversion
 */
export interface ConversionOptions {
  /** Auto-layout nodes (default: true) */
  autoLayout?: boolean;
  /** Version to set on output (default: '1.0') */
  version?: string;
}

/**
 * Context passed during recursive conversion
 */
interface ConversionContext {
  idGenerator: SequentialIdGenerator;
  nodes: WorkflowNodeJSON[];
  edges: WorkflowEdgeJSON[];
  lastNodeId: string | null;
  branchPath?: 'true' | 'false' | 'default';
  depth: number;
  currentY: number;
}

/**
 * Convert simplified workflow JSON to full format
 */
export function convertSimplifiedToFull(
  simplified: SimplifiedWorkflowJSON,
  options: ConversionOptions = {}
): WorkflowJSON {
  const { autoLayout = true, version = '1.0' } = options;

  const context: ConversionContext = {
    idGenerator: new SequentialIdGenerator(),
    nodes: [],
    edges: [],
    lastNodeId: null,
    depth: 0,
    currentY: START_Y,
  };

  // Convert steps recursively
  convertSteps(simplified.steps, context);

  // Apply auto-layout if enabled
  let finalNodes = context.nodes;
  if (autoLayout) {
    const layoutResult = autoLayoutNodes(context.nodes, context.edges);
    finalNodes = layoutResult.nodes;
  }

  return {
    version,
    playbookName: simplified.name,
    description: simplified.description,
    entityType: simplified.entityType || 'CONTACT',
    status: 'DRAFT',
    nodes: finalNodes,
    edges: context.edges,
    metadata: simplified.metadata,
  };
}

/**
 * Convert a list of steps, connecting them in sequence
 */
function convertSteps(
  steps: SimplifiedStepJSON[],
  context: ConversionContext
): string | null {
  let previousNodeId: string | null = context.lastNodeId;

  for (const step of steps) {
    const nodeId = convertStep(step, context, previousNodeId);
    previousNodeId = nodeId;
  }

  return previousNodeId;
}

/**
 * Convert a single step to a node
 */
function convertStep(
  step: SimplifiedStepJSON,
  context: ConversionContext,
  previousNodeId: string | null
): string {
  const nodeType = normalizeNodeType(step.type);
  const nodeId = context.idGenerator.next(nodeType);
  const label = step.name || getDefaultLabel(nodeType);

  // Create node data
  const nodeData: WorkflowNodeData = {
    nodeType,
    label,
    name: label,
    configured: hasRequiredConfig(step),
    parameters: step.config || {},
  };

  // Create node with temporary position (will be auto-laid out later)
  const node: WorkflowNodeJSON = {
    id: nodeId,
    type: nodeType,
    position: { x: START_X, y: context.currentY },
    data: nodeData,
  };

  context.nodes.push(node);
  context.currentY += NODE_HEIGHT + GAP_Y;

  // Connect to previous node
  if (previousNodeId) {
    const edge: WorkflowEdgeJSON = {
      id: context.idGenerator.next('edge'),
      source: previousNodeId,
      target: nodeId,
      sourceHandle: context.branchPath,
    };
    context.edges.push(edge);
  }

  // Handle conditional branches
  if (isConditionalStep(step)) {
    convertConditionalBranches(step, nodeId, context);
  }

  return nodeId;
}

/**
 * Convert conditional branches (onTrue/onFalse for if, cases for switch)
 */
function convertConditionalBranches(
  step: SimplifiedStepJSON,
  nodeId: string,
  context: ConversionContext
): void {
  const savedY = context.currentY;
  let mergeNodeId: string | null = null;

  // Handle if/condition branches
  if (step.onTrue || step.onFalse) {
    const branchResults: Array<{ lastNodeId: string | null; path: string }> = [];

    // Process TRUE branch
    if (step.onTrue && step.onTrue.length > 0) {
      const trueContext: ConversionContext = {
        ...context,
        lastNodeId: nodeId,
        branchPath: 'true',
        depth: context.depth + 1,
        currentY: savedY,
      };
      const lastTrueNode = convertSteps(step.onTrue, trueContext);
      context.nodes = trueContext.nodes;
      context.edges = trueContext.edges;
      context.currentY = Math.max(context.currentY, trueContext.currentY);

      if (lastTrueNode) {
        branchResults.push({ lastNodeId: lastTrueNode, path: 'true' });
      }
    } else {
      // Empty true branch - create edge to eventual merge point
      branchResults.push({ lastNodeId: nodeId, path: 'true' });
    }

    // Process FALSE branch
    if (step.onFalse && step.onFalse.length > 0) {
      const falseContext: ConversionContext = {
        ...context,
        lastNodeId: nodeId,
        branchPath: 'false',
        depth: context.depth + 1,
        currentY: savedY,
      };
      const lastFalseNode = convertSteps(step.onFalse, falseContext);
      context.nodes = falseContext.nodes;
      context.edges = falseContext.edges;
      context.currentY = Math.max(context.currentY, falseContext.currentY);

      if (lastFalseNode) {
        branchResults.push({ lastNodeId: lastFalseNode, path: 'false' });
      }
    } else {
      // Empty false branch - create edge to eventual merge point
      branchResults.push({ lastNodeId: nodeId, path: 'false' });
    }

    // Create merge node if there are branches to merge
    if (branchResults.length > 1 && branchResults.some((b) => b.lastNodeId !== nodeId)) {
      mergeNodeId = context.idGenerator.next('merge');
      const mergeNode: WorkflowNodeJSON = {
        id: mergeNodeId,
        type: 'merge',
        position: { x: START_X, y: context.currentY },
        data: {
          nodeType: 'merge',
          label: 'Merge',
          configured: true,
          parameters: { mode: 'append' },
        },
      };
      context.nodes.push(mergeNode);
      context.currentY += NODE_HEIGHT + GAP_Y;

      // Connect branches to merge
      branchResults.forEach(({ lastNodeId: branchLastNode, path }) => {
        if (branchLastNode) {
          const mergeEdge: WorkflowEdgeJSON = {
            id: context.idGenerator.next('edge'),
            source: branchLastNode,
            target: mergeNodeId!,
            sourceHandle: branchLastNode === nodeId ? path : undefined,
          };
          context.edges.push(mergeEdge);
        }
      });
    }
  }

  // Handle switch cases
  if (step.cases && step.cases.length > 0) {
    const caseResults: Array<{ lastNodeId: string | null; caseIndex: number }> = [];

    step.cases.forEach((caseItem, caseIndex) => {
      if (caseItem.steps && caseItem.steps.length > 0) {
        const caseContext: ConversionContext = {
          ...context,
          lastNodeId: nodeId,
          branchPath: `case-${caseIndex}` as 'true' | 'false' | 'default',
          depth: context.depth + 1,
          currentY: savedY + caseIndex * (NODE_HEIGHT + GAP_Y),
        };
        const lastCaseNode = convertSteps(caseItem.steps, caseContext);
        context.nodes = caseContext.nodes;
        context.edges = caseContext.edges;
        context.currentY = Math.max(context.currentY, caseContext.currentY);

        if (lastCaseNode) {
          caseResults.push({ lastNodeId: lastCaseNode, caseIndex });
        }
      }
    });

    // Handle default case
    if (step.defaultCase && step.defaultCase.length > 0) {
      const defaultContext: ConversionContext = {
        ...context,
        lastNodeId: nodeId,
        branchPath: 'default',
        depth: context.depth + 1,
        currentY: savedY,
      };
      const lastDefaultNode = convertSteps(step.defaultCase, defaultContext);
      context.nodes = defaultContext.nodes;
      context.edges = defaultContext.edges;
      context.currentY = Math.max(context.currentY, defaultContext.currentY);

      if (lastDefaultNode) {
        caseResults.push({ lastNodeId: lastDefaultNode, caseIndex: -1 });
      }
    }

    // Create merge node if there are multiple cases
    if (caseResults.length > 1) {
      mergeNodeId = context.idGenerator.next('merge');
      const mergeNode: WorkflowNodeJSON = {
        id: mergeNodeId,
        type: 'merge',
        position: { x: START_X, y: context.currentY },
        data: {
          nodeType: 'merge',
          label: 'Merge',
          configured: true,
          parameters: { mode: 'append' },
        },
      };
      context.nodes.push(mergeNode);
      context.currentY += NODE_HEIGHT + GAP_Y;

      // Connect cases to merge
      caseResults.forEach(({ lastNodeId: caseLastNode }) => {
        if (caseLastNode) {
          const mergeEdge: WorkflowEdgeJSON = {
            id: context.idGenerator.next('edge'),
            source: caseLastNode,
            target: mergeNodeId!,
          };
          context.edges.push(mergeEdge);
        }
      });
    }
  }

  // Update context with merge node as last node for subsequent steps
  if (mergeNodeId) {
    context.lastNodeId = mergeNodeId;
  }
}

/**
 * Check if a step is conditional (has branches)
 */
function isConditionalStep(step: SimplifiedStepJSON): boolean {
  return Boolean(
    step.onTrue ||
      step.onFalse ||
      (step.cases && step.cases.length > 0) ||
      step.defaultCase
  );
}

/**
 * Normalize node type to canonical form
 * Maps aliases and shorthand names to the actual node types used in the system
 */
function normalizeNodeType(type: string): string {
  // Map common aliases to canonical node types
  // Based on PlaybookWorkflow/constants.ts definitions
  const aliases: Record<string, string> = {
    // Trigger aliases
    trigger: 'manualTrigger',
    // NOTE: 'manual' below still maps to scheduleTrigger, same as before this
    // fix - untested and left alone (smallest correct change for the failing
    // test, which only covers 'trigger'). Likely the same bug as 'trigger'
    // was; flag before relying on it.
    manual: 'scheduleTrigger',
    schedule: 'scheduleTrigger',
    cron: 'scheduleTrigger',
    webhook: 'webhookTrigger',

    // Trigger node name variations (without Node suffix -> with Node suffix)
    accountCreated: 'accountCreatedNode',
    accountUpdated: 'accountUpdatedNode',
    contactCreated: 'contactCreatedNode',
    contactUpdated: 'contactUpdatedNode',
    cmpEngagement: 'cmpEngagementNode',

    // Communication aliases
    http: 'httpRequest',
    httpNode: 'httpRequest',
    httpRequestNode: 'httpRequest',
    email: 'gmail',
    sendEmail: 'gmail',
    gmailNode: 'gmail',
    slackNode: 'slack',

    // Google aliases
    googleSheet: 'googleSheets',
    googleSheetNode: 'googleSheets',
    sheets: 'googleSheets',

    // AI aliases
    llm: 'openai',
    ai: 'openai',
    gpt: 'openai',
    claude: 'anthropic',
    anthropicNode: 'anthropic',
    openaiNode: 'openai',
    hpm: 'hpmNode',
    personalizedMessage: 'hpmNode',
    generateMessage: 'hpmNode',

    // Logic aliases
    branch: 'if',
    conditional: 'if',
    condition: 'if',
    conditionNode: 'if',
    mergeNode: 'merge',
    switchNode: 'switch',

    // Platform node name variations (without Node suffix -> with Node suffix)
    moveToTAL: 'moveToTALNode',
    addContactToTal: 'moveToTALNode',
    addToTal: 'moveToTALNode',
    moveToAccountTAL: 'moveToAccountTALNode',
    addAccountToTal: 'moveToAccountTALNode',
    sourceContact: 'sourceContactNode',
    assignContact: 'assignContactNode',
    criteriaContact: 'criteriaContactNode',
    criteriaAccount: 'criteriaAccountNode',
    enrichDomain: 'enrichDomainNode',
    personLinkedinLookup: 'enrichDomainNode',
    linkedinLookup: 'enrichDomainNode',

    // Platform nodes that don't have Node suffix in canonical form
    addToCampaignNode: 'addToCampaign',
    enrichContactNode: 'enrichContact',
  };

  // Check lowercase version first, then original
  return aliases[type.toLowerCase()] || aliases[type] || type;
}

/**
 * Get default label for a node type
 * Labels match the names in PlaybookWorkflow/constants.ts
 */
function getDefaultLabel(nodeType: string): string {
  const labels: Record<string, string> = {
    // Triggers
    scheduleTrigger: 'Schedule Trigger',
    googleSheetsTrigger: 'Google Sheets Trigger',
    accountCreatedNode: 'Account Created',
    accountUpdatedNode: 'Account Updated',
    contactCreatedNode: 'Contact Created',
    contactUpdatedNode: 'Contact Updated',
    cmpEngagementNode: 'CMP Engagement Trigger',
    webhookTrigger: 'Webhook Trigger',
    gmailTrigger: 'Gmail Trigger',

    // Logic
    if: 'IF',
    switch: 'Switch',
    merge: 'Merge',
    set: 'Set',
    mapFields: 'Map Fields',

    // Communication
    httpRequest: 'HTTP Request',
    gmail: 'Gmail',
    slack: 'Slack',
    teams: 'Microsoft Teams',

    // Productivity
    googleSheets: 'Google Sheets',

    // AI
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    hpmNode: 'Generated personalized message',

    // Platform
    addToCampaign: 'Add to Campaign',
    enrichContact: 'Enrich Contact',
    enrichDomainNode: 'Person Linkedin Lookup',
    moveToTALNode: 'Add Contact to TAL',
    moveToAccountTALNode: 'Add Account to TAL',
    criteriaAccountNode: 'Account Criteria',
    criteriaContactNode: 'Contact Criteria',
    sourceContactNode: 'Source Contact',
    assignContactNode: 'Assign Contact Owner',

    // Legacy names (for backwards compatibility)
    accountCreated: 'Account Created',
    accountUpdated: 'Account Updated',
    contactCreated: 'Contact Created',
    contactUpdated: 'Contact Updated',
    hpm: 'Generated personalized message',
    moveToTAL: 'Add Contact to TAL',
    moveToAccountTAL: 'Add Account to TAL',
    criteriaAccount: 'Account Criteria',
    criteriaContact: 'Contact Criteria',
    sourceContact: 'Source Contact',
    assignContact: 'Assign Contact Owner',
    enrichDomain: 'Person Linkedin Lookup',
  };

  return labels[nodeType] || nodeType.replace(/([A-Z])/g, ' $1').trim();
}

/**
 * Check if step has required configuration
 */
function hasRequiredConfig(step: SimplifiedStepJSON): boolean {
  // Simple heuristic: if config has any keys, consider it configured
  return Boolean(step.config && Object.keys(step.config).length > 0);
}

/**
 * Convert full format back to simplified format
 */
export function convertFullToSimplified(
  workflow: WorkflowJSON
): SimplifiedWorkflowJSON {
  const nodeMap = new Map(workflow.nodes.map((n) => [n.id, n]));

  // Build adjacency structures
  const outgoingMap = new Map<string, WorkflowEdgeJSON[]>();
  workflow.nodes.forEach((node) => outgoingMap.set(node.id, []));
  workflow.edges.forEach((edge) => {
    const existing = outgoingMap.get(edge.source) || [];
    existing.push(edge);
    outgoingMap.set(edge.source, existing);
  });

  // Find start node (first trigger or first node)
  // Based on PlaybookWorkflow/constants.ts trigger types
  const triggerTypes = [
    'scheduleTrigger',
    'googleSheetsTrigger',
    'accountCreatedNode',
    'accountUpdatedNode',
    'contactCreatedNode',
    'contactUpdatedNode',
    'cmpEngagementNode',
    'webhookTrigger',
    'gmailTrigger',
    // Legacy names without Node suffix
    'accountCreated',
    'accountUpdated',
    'contactCreated',
    'contactUpdated',
  ];

  let startNode = workflow.nodes.find((n) => triggerTypes.includes(n.type));
  if (!startNode && workflow.nodes.length > 0) {
    startNode = workflow.nodes[0];
  }

  // Convert nodes to steps
  const visited = new Set<string>();
  const steps: SimplifiedStepJSON[] = [];

  if (startNode) {
    convertNodeToStep(startNode.id, nodeMap, outgoingMap, visited, steps);
  }

  return {
    name: workflow.playbookName,
    description: workflow.description,
    entityType: workflow.entityType,
    steps,
    metadata: workflow.metadata,
  };
}

/**
 * Recursively convert a node and its successors to steps
 */
function convertNodeToStep(
  nodeId: string,
  nodeMap: Map<string, WorkflowNodeJSON>,
  outgoingMap: Map<string, WorkflowEdgeJSON[]>,
  visited: Set<string>,
  steps: SimplifiedStepJSON[]
): void {
  if (visited.has(nodeId)) {
    return;
  }
  visited.add(nodeId);

  const node = nodeMap.get(nodeId);
  if (!node) return;

  // Skip merge nodes in simplified format
  if (node.type === 'merge' || node.type === 'mergeNode') {
    // Continue with successors
    const outgoing = outgoingMap.get(nodeId) || [];
    outgoing.forEach((edge) => {
      convertNodeToStep(edge.target, nodeMap, outgoingMap, visited, steps);
    });
    return;
  }

  const step: SimplifiedStepJSON = {
    type: node.type,
    name: node.data?.label || node.data?.name,
    config: (node.data?.parameters || {}) as Record<string, unknown>,
  };

  // Handle conditional nodes
  const conditionalTypes = ['if', 'condition', 'conditionNode', 'switch'];
  if (conditionalTypes.includes(node.type)) {
    const outgoing = outgoingMap.get(nodeId) || [];

    if (node.type === 'switch') {
      // Handle switch cases
      const caseEdges = outgoing.filter(
        (e) => e.sourceHandle?.startsWith('case-') || e.sourceHandle === 'default'
      );

      const cases: Array<{ value: string; steps: SimplifiedStepJSON[] }> = [];
      const defaultSteps: SimplifiedStepJSON[] = [];

      caseEdges.forEach((edge) => {
        const branchSteps: SimplifiedStepJSON[] = [];
        convertNodeToStep(edge.target, nodeMap, outgoingMap, visited, branchSteps);

        if (edge.sourceHandle === 'default') {
          defaultSteps.push(...branchSteps);
        } else {
          const caseIndex = parseInt(edge.sourceHandle?.replace('case-', '') || '0');
          cases[caseIndex] = {
            value: `case${caseIndex}`,
            steps: branchSteps,
          };
        }
      });

      if (cases.length > 0) step.cases = cases.filter(Boolean);
      if (defaultSteps.length > 0) step.defaultCase = defaultSteps;
    } else {
      // Handle if/condition
      const trueEdge = outgoing.find((e) => e.sourceHandle === 'true');
      const falseEdge = outgoing.find((e) => e.sourceHandle === 'false');

      if (trueEdge) {
        const trueSteps: SimplifiedStepJSON[] = [];
        convertNodeToStep(trueEdge.target, nodeMap, outgoingMap, visited, trueSteps);
        if (trueSteps.length > 0) step.onTrue = trueSteps;
      }

      if (falseEdge) {
        const falseSteps: SimplifiedStepJSON[] = [];
        convertNodeToStep(falseEdge.target, nodeMap, outgoingMap, visited, falseSteps);
        if (falseSteps.length > 0) step.onFalse = falseSteps;
      }
    }
  } else {
    // Continue with successors for non-conditional nodes
    const outgoing = outgoingMap.get(nodeId) || [];
    if (outgoing.length > 0) {
      steps.push(step);
      outgoing.forEach((edge) => {
        convertNodeToStep(edge.target, nodeMap, outgoingMap, visited, steps);
      });
      return;
    }
  }

  steps.push(step);
}
